import { useEffect, useState, useRef } from 'react';

interface AddressMapProps {
  address: string;
}

const SDK_URL = 'https://dapi.kakao.com/v2/maps/sdk.js?appkey=032aee3d910c25f34f64cd2fb353616e&libraries=services&autoload=false&_v=20260301';

// ExposureMap과 공유하는 localStorage 지오코딩 캐시
const CACHE_KEY = 'str_geocode_cache';
const CACHE_TTL = 30 * 24 * 60 * 60 * 1000; // 30일

const loadCache = (): Record<string, { lat: number; lng: number; ts: number }> => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
  } catch {
    return {};
  }
};

const saveCache = (cache: Record<string, { lat: number; lng: number; ts: number }>) => {
  localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
};

const AddressMap = ({ address }: AddressMapProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<kakao.maps.Map | null>(null);
  const markerRef = useRef<kakao.maps.Marker | null>(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 카카오맵 SDK 로드
  useEffect(() => {
    const initSdk = () => {
      if (window.kakao && window.kakao.maps) {
        window.kakao.maps.load(() => setSdkReady(true));
        return;
      }
      const script = document.createElement('script');
      script.src = SDK_URL;
      script.onload = () => {
        if (window.kakao && window.kakao.maps) {
          window.kakao.maps.load(() => setSdkReady(true));
        } else {
          setSdkError(true);
          setLoading(false);
        }
      };
      script.onerror = () => {
        setSdkError(true);
        setLoading(false);
      };
      document.head.appendChild(script);
    };

    const timer = setTimeout(initSdk, 500);
    if (window.kakao && window.kakao.maps) {
      clearTimeout(timer);
      initSdk();
    }
    return () => clearTimeout(timer);
  }, []);

  // 주소 지오코딩 + 지도 표시
  useEffect(() => {
    if (!sdkReady || !containerRef.current || !address) {
      if (!address) setLoading(false);
      return;
    }

    const geocoder = new kakao.maps.services.Geocoder();

    // 캐시 확인
    const cache = loadCache();
    const entry = cache[address];
    if (entry && Date.now() - entry.ts < CACHE_TTL) {
      showMap(entry.lat, entry.lng);
      return;
    }

    // 카카오 지오코딩
    geocoder.addressSearch(address, (result, status) => {
      if (status === kakao.maps.services.Status.OK && result.length > 0) {
        const lat = parseFloat(result[0].y);
        const lng = parseFloat(result[0].x);
        // 캐시 저장
        const freshCache = loadCache();
        freshCache[address] = { lat, lng, ts: Date.now() };
        saveCache(freshCache);
        showMap(lat, lng);
      } else {
        setError('주소를 찾을 수 없습니다.');
        setLoading(false);
      }
    });

    function showMap(lat: number, lng: number) {
      const position = new kakao.maps.LatLng(lat, lng);

      if (!mapRef.current && containerRef.current) {
        const map = new kakao.maps.Map(containerRef.current, {
          center: position,
          level: 3,
        });
        mapRef.current = map;

        const marker = new kakao.maps.Marker({ position, map });
        markerRef.current = marker;
      } else if (mapRef.current) {
        mapRef.current.setCenter(position);
        markerRef.current?.setPosition(position);
      }

      setLoading(false);
      setError(null);
    }
  }, [sdkReady, address]);

  if (sdkError) {
    return (
      <div style={{
        width: '100%', height: '300px', display: 'flex', alignItems: 'center',
        justifyContent: 'center', backgroundColor: '#f8f8f8', borderRadius: '8px',
        border: '1px solid #e0e0e0'
      }}>
        <p style={{ color: '#e53935' }}>카카오맵을 불러올 수 없습니다.</p>
      </div>
    );
  }

  if (error && !loading) {
    return (
      <div style={{
        width: '100%', height: '300px', display: 'flex', alignItems: 'center',
        justifyContent: 'center', backgroundColor: '#f8f8f8', borderRadius: '8px',
        border: '1px solid #e0e0e0'
      }}>
        <p style={{ color: '#999' }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%', height: '300px', borderRadius: '8px', overflow: 'hidden',
      border: '1px solid #e0e0e0', position: 'relative'
    }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      {loading && (
        <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'rgba(248,248,248,0.9)'
        }}>
          <p style={{ color: '#666' }}>지도 로딩 중...</p>
        </div>
      )}
    </div>
  );
};

export default AddressMap;
